import {
  AddOutlined,
  ArchiveOutlined,
  AutoAwesomeMotionOutlined,
  DeleteForeverOutlined,
  DomainOutlined,
  EditOutlined,
  ExpandMoreOutlined,
  LocationOnOutlined,
  WarehouseOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CreateWarehouseNode,
  WarehouseNodeImpact,
  WarehouseNode,
  archiveWarehouseNode,
  createWarehouseNode,
  deleteWarehouseNodePermanently,
  isAuthenticationError,
  loadWarehouseNodeImpact,
  loadWarehouseStructure,
  updateWarehouseNode
} from "./api";
import { login } from "./auth";
import { BinGeneratorDialog } from "./BinGeneratorDialog";
import { BinDetailDialog } from "./BinDetailDialog";
import { PageHeader } from "./PageHeader";
import {
  measurementInputStep, measurementInputValue,
  measurementUnit, toMetric
} from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";

const demoOrganizationId = "10000000-0000-0000-0000-000000000001";
const types: WarehouseNode["type"][] = [
  "SITE", "WAREHOUSE", "STORAGE_AREA", "STORAGE_SECTION", "STORAGE_BIN"
];
const parentTypes: Partial<Record<WarehouseNode["type"], WarehouseNode["type"]>> = {
  WAREHOUSE: "SITE",
  STORAGE_AREA: "WAREHOUSE",
  STORAGE_SECTION: "STORAGE_AREA"
};
const classificationOptions: Partial<Record<WarehouseNode["type"], string[]>> = {
  STORAGE_AREA: [
    "HIGH_RACK", "SHELF", "BULK", "FLOOR", "PICKING", "QUARANTINE",
    "CLIMATE", "ESD", "HAZMAT", "STAGING", "OTHER"
  ],
  STORAGE_SECTION: [
    "STANDARD", "FAST_MOVING", "SLOW_MOVING", "HEAVY", "BULKY",
    "SECURE", "QUALITY", "OTHER"
  ],
  STORAGE_BIN: [
    "STORAGE", "PICK_FACE", "FLOOR", "STAGING", "QUALITY",
    "WORK", "VIRTUAL"
  ]
};

function numberAttribute(value: unknown): number | undefined {
  return value === null || value === undefined || value === ""
    ? undefined : Number(value);
}

function stringAttribute(value: unknown): string | undefined {
  return value === null || value === undefined || value === ""
    ? undefined : String(value);
}

interface Props {
  onBack: () => void;
  initialBinId?: string | null;
}

export function WarehouseStructure({ onBack, initialBinId = null }: Props) {
  const { t } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const lengthUnit = measurementUnit("LENGTH", measurements);
  const massUnit = measurementUnit("MASS", measurements);
  const lengthStep = measurementInputStep("LENGTH", measurements);
  const massStep = measurementInputStep("MASS", measurements);
  const localizedLabels = Object.fromEntries(types.map((value) => [
    value, t(`masterData.warehouse.types.${value}`)
  ])) as Record<WarehouseNode["type"], string>;
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [selectedBinId, setSelectedBinId] = useState<string | null>(null);
  const [type, setType] = useState<WarehouseNode["type"]>("WAREHOUSE");
  const [parentId, setParentId] = useState("");
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [classification, setClassification] = useState("");
  const [aisle, setAisle] = useState("");
  const [bay, setBay] = useState("");
  const [level, setLevel] = useState("");
  const [position, setPosition] = useState("");
  const [travelSequence, setTravelSequence] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [maxWeight, setMaxWeight] = useState("");
  const [managedNode, setManagedNode] = useState<WarehouseNode | null>(null);
  const [managementReason, setManagementReason] = useState("");
  const [managementName, setManagementName] = useState("");
  const [managementClassification, setManagementClassification] = useState("");
  const [managementAttributes, setManagementAttributes] =
    useState<Record<string, unknown>>({});

  const structure = useQuery({
    queryKey: ["warehouse-structure"],
    queryFn: loadWarehouseStructure
  });
  useEffect(() => {
    if (initialBinId) setSelectedBinId(initialBinId);
  }, [initialBinId]);
  const nodes = structure.data ?? [];
  const eligibleParents = nodes.filter((node) =>
    type === "STORAGE_BIN"
      ? node.type === "STORAGE_AREA" || node.type === "STORAGE_SECTION"
      : node.type === parentTypes[type]
  );
  const roots = useMemo(() => nodes.filter((node) => node.parentId === null), [nodes]);
  const impact = useQuery<WarehouseNodeImpact>({
    queryKey: ["warehouse-node-impact", managedNode?.id],
    queryFn: () => loadWarehouseNodeImpact(managedNode!.id),
    enabled: managedNode !== null
  });

  const createNode = useMutation({
    mutationFn: createWarehouseNode,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["warehouse-structure"] });
      setDialogOpen(false);
      setCode("");
      setDisplayName("");
      setClassification("");
      setAisle("");
      setBay("");
      setLevel("");
      setPosition("");
      setTravelSequence("");
      setLength("");
      setWidth("");
      setHeight("");
      setMaxWeight("");
    }
  });
  const updateNode = useMutation({
    mutationFn: () => updateWarehouseNode(managedNode!.id, {
      displayName: managementName,
      classification: managementClassification || undefined,
      travelSequence: numberAttribute(managedNode?.type === "STORAGE_SECTION"
        ? managementAttributes.putawayPriority
        : managementAttributes.travelSequence),
      aisleCode: stringAttribute(managementAttributes.aisle),
      bayCode: stringAttribute(managementAttributes.bay),
      levelCode: stringAttribute(managementAttributes.level),
      positionCode: stringAttribute(managementAttributes.position),
      usableLengthMm: metricAttribute(managementAttributes.usableLengthMm, "LENGTH"),
      usableWidthMm: metricAttribute(managementAttributes.usableWidthMm, "LENGTH"),
      usableHeightMm: metricAttribute(managementAttributes.usableHeightMm, "LENGTH"),
      openingWidthMm: metricAttribute(managementAttributes.openingWidthMm, "LENGTH"),
      openingHeightMm: metricAttribute(managementAttributes.openingHeightMm, "LENGTH"),
      maxWeightKg: metricAttribute(managementAttributes.maxWeightKg, "MASS"),
      maxHandlingUnits: numberAttribute(managementAttributes.maxHandlingUnits),
      version: managedNode!.version,
      reason: managementReason
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["warehouse-structure"] });
      setManagedNode(null);
    }
  });
  const archiveNode = useMutation({
    mutationFn: () => archiveWarehouseNode(
      managedNode!.id, managedNode!.version, managementReason
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["warehouse-structure"] });
      setManagedNode(null);
    }
  });
  const permanentDelete = useMutation({
    mutationFn: () => deleteWarehouseNodePermanently(
      managedNode!.id, managedNode!.version, managementReason
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["warehouse-structure"] });
      setManagedNode(null);
    }
  });

  function openManagement(node: WarehouseNode) {
    const attributes = JSON.parse(node.attributes) as Record<string, unknown>;
    (["usableLengthMm", "usableWidthMm", "usableHeightMm",
      "openingWidthMm", "openingHeightMm"] as const).forEach((field) => {
      const value = numberAttribute(attributes[field]);
      if (value !== undefined)
        attributes[field] = measurementInputValue(value, "LENGTH", measurements);
    });
    const weight = numberAttribute(attributes.maxWeightKg);
    if (weight !== undefined)
      attributes.maxWeightKg = measurementInputValue(weight, "MASS", measurements);
    setManagedNode(node);
    setManagementName(node.displayName);
    setManagementClassification(
      String(attributes.areaType ?? attributes.purpose
        ?? attributes.binType ?? "")
    );
    setManagementAttributes(attributes);
    setManagementReason("");
  }

  function changeType(nextType: WarehouseNode["type"]) {
    setType(nextType);
    setParentId("");
    setClassification("");
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const request: CreateWarehouseNode = {
      organizationId: demoOrganizationId,
      parentId: type === "SITE" ? null : parentId,
      type,
      code,
      displayName,
      classification: classification || undefined,
      aisleCode: aisle || undefined,
      bayCode: bay || undefined,
      levelCode: level || undefined,
      positionCode: position || undefined,
      travelSequence: travelSequence ? Number(travelSequence) : undefined,
      usableLengthMm: length ? toMetric(Number(length), "LENGTH", measurements) : undefined,
      usableWidthMm: width ? toMetric(Number(width), "LENGTH", measurements) : undefined,
      usableHeightMm: height ? toMetric(Number(height), "LENGTH", measurements) : undefined,
      maxWeightKg: maxWeight ? toMetric(Number(maxWeight), "MASS", measurements) : undefined,
      customData: {}
    };
    createNode.mutate(request);
  }

  function metricAttribute(value: unknown, kind: "LENGTH" | "MASS") {
    const parsed = numberAttribute(value);
    return parsed === undefined ? undefined : toMetric(parsed, kind, measurements);
  }

  function renderNode(node: WarehouseNode, depth = 0) {
    const children = nodes.filter((candidate) => candidate.parentId === node.id);
    return (
      <Box key={node.id} sx={{ ml: { xs: depth ? 1.5 : 0, sm: depth * 2.5 } }}>
        <Card variant="outlined"
          onClick={() => node.type === "STORAGE_BIN" && setSelectedBinId(node.id)}
          sx={{
            mb: 1,
            borderColor: depth === 0 ? "secondary.main" : "divider",
            cursor: node.type === "STORAGE_BIN" ? "pointer" : "default"
          }}>
          <CardContent sx={{ py: "12px !important" }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {node.type === "STORAGE_BIN" ? (
                <LocationOnOutlined color="secondary" />
              ) : node.type === "WAREHOUSE" ? (
                <WarehouseOutlined color="primary" />
              ) : (
                <DomainOutlined color="action" />
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>{node.code} · {node.displayName}</Typography>
                <Typography variant="caption" color="text.secondary">{localizedLabels[node.type]}</Typography>
              </Box>
              <Chip size="small" label={t(`masterData.common.statuses.${node.status}`, { defaultValue: node.status })} color={node.status === "ACTIVE" ? "success" : "default"} />
              <Button size="small" variant="outlined" startIcon={<EditOutlined />}
                data-testid={`warehouse-manage-${node.code}`}
                onClick={(event) => {
                  event.stopPropagation();
                  openManagement(node);
                }}>
                {t("masterData.common.manage")}
              </Button>
              {children.length > 0 && <ExpandMoreOutlined color="action" />}
            </Stack>
          </CardContent>
        </Card>
        {children.map((child) => renderNode(child, depth + 1))}
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} sx={{
      height: { md: "100%" }, minHeight: 0
    }}>
      <PageHeader
        title={t("masterData.warehouse.title")}
        compact
        onBack={onBack}
        actions={
          <>
          <Button variant="outlined" startIcon={<AutoAwesomeMotionOutlined />}
            onClick={() => setGeneratorOpen(true)}>
            {t("masterData.warehouse.generateBins")}
          </Button>
          <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setDialogOpen(true)}>
            {t("masterData.common.add")}
          </Button>
          </>
        }
      />

      {structure.isLoading && <CircularProgress />}
      {structure.isError && (
        <Alert
          severity="error"
          action={isAuthenticationError(structure.error)
            ? <Button color="inherit" size="small" onClick={() => void login()}>
                {t("masterData.common.reconnect")}
              </Button>
            : <Button color="inherit" size="small" onClick={() => void structure.refetch()}>
                {t("masterData.common.retry")}
              </Button>}
        >
          {isAuthenticationError(structure.error)
            ? t("masterData.common.sessionExpired")
            : t("masterData.warehouse.loadError")}
        </Alert>
      )}
      {!structure.isLoading && !structure.isError && roots.length === 0 && (
        <Alert severity="info">{t("masterData.warehouse.firstSite")}</Alert>
      )}
      <Box sx={{
        minHeight: 0, flex: { md: 1 }, overflowY: { md: "auto" },
        pr: { md: .5 }
      }}>
      {roots.map((root) => renderNode(root))}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>{t("masterData.warehouse.new")}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t("masterData.common.type")}</InputLabel>
                <Select value={type} label={t("masterData.common.type")} onChange={(event) =>
                  changeType(event.target.value as WarehouseNode["type"])
                }>
                  {types.map((value) => <MenuItem key={value} value={value}>{localizedLabels[value]}</MenuItem>)}
                </Select>
              </FormControl>
              {type !== "SITE" && (
                <FormControl fullWidth required>
                  <InputLabel>{t("masterData.warehouse.parent")}</InputLabel>
                  <Select value={parentId} label={t("masterData.warehouse.parent")} onChange={(event) => setParentId(event.target.value)}>
                    {eligibleParents.map((node) => (
                      <MenuItem key={node.id} value={node.id}>{node.code} · {node.displayName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField label={t("masterData.common.code")} value={code} required inputProps={{ maxLength: 100 }}
                helperText={t("masterData.warehouse.codeHelp")}
                onChange={(event) => setCode(event.target.value.toUpperCase())} />
              <TextField label={t("masterData.common.name")} value={displayName} required
                onChange={(event) => setDisplayName(event.target.value)} />
              {(type === "STORAGE_AREA" || type === "STORAGE_SECTION" || type === "STORAGE_BIN") && (
                <FormControl fullWidth>
                  <InputLabel>{t("masterData.warehouse.classification")}</InputLabel>
                  <Select value={classification} label={t("masterData.warehouse.classification")}
                    onChange={(event) => setClassification(event.target.value)}>
                    <MenuItem value="">
                      <em>{t("masterData.warehouse.defaultValue")}</em>
                    </MenuItem>
                    {classificationOptions[type]?.map((value) => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {type === "STORAGE_BIN" && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField label={t("masterData.warehouse.aisle")} value={aisle} onChange={(e) => setAisle(e.target.value)} />
                    <TextField label={t("masterData.warehouse.bay")} value={bay} onChange={(e) => setBay(e.target.value)} />
                    <TextField label={t("masterData.warehouse.level")} value={level} onChange={(e) => setLevel(e.target.value)} />
                    <TextField label={t("masterData.warehouse.position")} value={position} onChange={(e) => setPosition(e.target.value)} />
                  </Stack>
                  <TextField type="number" label={t("masterData.warehouse.pickingSequence")}
                    value={travelSequence}
                    inputProps={{ min: 1, step: 1 }}
                    helperText={t("masterData.warehouse.visitOrderHelp")}
                    onChange={(e) => setTravelSequence(e.target.value)} />
                  <Typography variant="subtitle2">{t("masterData.warehouse.dimensions")}</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField type="number" label={`${t("masterData.warehouse.length")} (${lengthUnit})`} value={length}
                      inputProps={{ min: lengthStep, step: lengthStep }}
                      onChange={(e) => setLength(e.target.value)} />
                    <TextField type="number" label={`${t("masterData.warehouse.width")} (${lengthUnit})`} value={width}
                      inputProps={{ min: lengthStep, step: lengthStep }}
                      onChange={(e) => setWidth(e.target.value)} />
                    <TextField type="number" label={`${t("masterData.warehouse.height")} (${lengthUnit})`} value={height}
                      inputProps={{ min: lengthStep, step: lengthStep }}
                      onChange={(e) => setHeight(e.target.value)} />
                    <TextField type="number" label={`${t("masterData.warehouse.maxWeight")} (${massUnit})`} value={maxWeight}
                      inputProps={{ min: massStep, step: massStep }}
                      onChange={(e) => setMaxWeight(e.target.value)} />
                  </Stack>
                </>
              )}
              {createNode.isError && (
                <Alert severity="error">
                  {createNode.error instanceof Error
                    ? createNode.error.message
                    : t("masterData.common.createError")}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("masterData.common.cancel")}</Button>
            <Button type="submit" variant="contained"
              disabled={createNode.isPending || (type !== "SITE" && !parentId)}>
              {t("masterData.common.create")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={managedNode !== null}
        onClose={() => setManagedNode(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {t("masterData.warehouse.manageTitle", { type: managedNode ? localizedLabels[managedNode.type].toLowerCase() : "" })}
          {managedNode ? ` · ${managedNode.code}` : ""}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            <Alert severity="info">
              {t("masterData.warehouse.immutableHelp")}
            </Alert>
            <TextField label={t("masterData.common.code")} value={managedNode?.code ?? ""}
              data-testid="warehouse-managed-code"
              disabled fullWidth />
            <TextField label={t("masterData.common.name")} value={managementName} required
              onChange={(event) => setManagementName(event.target.value)} />
            {managedNode && classificationOptions[managedNode.type] && (
              <FormControl fullWidth>
                <InputLabel>{t("masterData.warehouse.classification")}</InputLabel>
                <Select label={t("masterData.warehouse.classification")}
                  value={managementClassification}
                  onChange={(event) =>
                    setManagementClassification(event.target.value)}>
                  {classificationOptions[managedNode.type]?.map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {managedNode?.type === "STORAGE_BIN" && (
              <>
                <Typography variant="subtitle2">
                  {t("masterData.warehouse.routeDimensions")}
                </Typography>
                <TextField type="number" label={t("masterData.warehouse.pickingSequence")}
                  value={String(managementAttributes.travelSequence ?? "")}
                  inputProps={{ min: 1, step: 1 }}
                  helperText={t("masterData.warehouse.routeHelp")}
                  onChange={(event) => setManagementAttributes(
                    (current) => ({
                      ...current, travelSequence: event.target.value
                    })
                  )} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  {(["aisle", "bay", "level", "position"] as const).map(
                    (field) => (
                      <TextField key={field}
                        label={t(`masterData.warehouse.coordinates.${field}`)}
                        value={String(managementAttributes[field] ?? "")}
                        onChange={(event) => setManagementAttributes(
                          (current) => ({
                            ...current, [field]: event.target.value
                          })
                        )} />
                    )
                  )}
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  {([
                    ["usableLengthMm", `${t("masterData.warehouse.usableLength")} (${lengthUnit})`],
                    ["usableWidthMm", `${t("masterData.warehouse.usableWidth")} (${lengthUnit})`],
                    ["usableHeightMm", `${t("masterData.warehouse.usableHeight")} (${lengthUnit})`],
                    ["openingWidthMm", `${t("masterData.warehouse.openingWidth")} (${lengthUnit})`],
                    ["openingHeightMm", `${t("masterData.warehouse.openingHeight")} (${lengthUnit})`],
                    ["maxWeightKg", `${t("masterData.warehouse.maximumWeight")} (${massUnit})`],
                    ["maxHandlingUnits", "Nombre maximal de HU"]
                  ] as const).map(([field, label]) => (
                    <TextField key={field} type="number" label={label}
                      inputProps={{
                        min: field === "maxHandlingUnits" ? 1
                          : field === "maxWeightKg" ? massStep : lengthStep,
                        step: field === "maxHandlingUnits" ? 1
                          : field === "maxWeightKg" ? massStep : lengthStep
                      }}
                      value={String(managementAttributes[field] ?? "")}
                      onChange={(event) => setManagementAttributes(
                        (current) => ({
                          ...current, [field]: event.target.value
                        })
                      )} />
                  ))}
                </Stack>
              </>
            )}
            <TextField label={t("masterData.warehouse.reason")} multiline minRows={2}
              data-testid="warehouse-management-reason"
              value={managementReason}
              onChange={(event) => setManagementReason(event.target.value)}
              helperText={t("masterData.common.requiredMin3")} />
            {impact.isLoading && <CircularProgress size={24} />}
            {impact.data && (
              <Card variant="outlined">
                <CardContent>
                  <Typography fontWeight={800} gutterBottom>
                    {t("masterData.warehouse.integrityCheck")}
                  </Typography>
                  {impact.data.blockers.length > 0 ? (
                    <Alert severity="warning">
                      {impact.data.blockers.join(" · ")}
                    </Alert>
                  ) : (
                    <Alert severity="success">
                      {t("masterData.warehouse.noBlocker")}
                    </Alert>
                  )}
                  {impact.data.historicalReferenceCount > 0 && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ mt: 1 }}>
                      {t("masterData.warehouse.historicalReferences", { count: impact.data.historicalReferenceCount })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
            {(updateNode.isError || archiveNode.isError
              || permanentDelete.isError) && (
              <Alert severity="error">
                {[updateNode.error, archiveNode.error, permanentDelete.error]
                  .find((error) => error instanceof Error)?.message
                  ?? t("masterData.common.operationError")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap" }}>
          <Button color="error" startIcon={<DeleteForeverOutlined />}
            disabled={!impact.data?.permanentDeleteAllowed
              || managementReason.trim().length < 3
              || permanentDelete.isPending}
            onClick={() => permanentDelete.mutate()}>
            {t("masterData.common.deletePermanently")}
          </Button>
          <Button color="warning" startIcon={<ArchiveOutlined />}
            disabled={!impact.data?.archiveAllowed
              || managedNode?.status === "INACTIVE"
              || managementReason.trim().length < 3
              || archiveNode.isPending}
            onClick={() => archiveNode.mutate()}>
            {t("masterData.common.archive")}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setManagedNode(null)}>{t("masterData.common.close")}</Button>
          <Button variant="contained" startIcon={<EditOutlined />}
            data-testid="warehouse-save"
            disabled={!managementName.trim()
              || managementReason.trim().length < 3
              || managedNode?.status === "INACTIVE"
              || updateNode.isPending}
            onClick={() => updateNode.mutate()}>
            {t("masterData.common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <BinGeneratorDialog
        open={generatorOpen}
        nodes={nodes}
        onClose={() => setGeneratorOpen(false)}
        onGenerated={async () => {
          await queryClient.invalidateQueries({ queryKey: ["warehouse-structure"] });
        }}
      />
      <BinDetailDialog binId={selectedBinId} onClose={() => setSelectedBinId(null)} />
    </Stack>
  );
}
